import { useCallback, useEffect, useState } from 'react';
import { budget, type Category } from '../lib/budget';
import { useLiveReload } from '../lib/live-context';
import { parseAmountToCents } from '../lib/money';

export function Categories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [name, setName] = useState('');
  const [limit, setLimit] = useState('');
  const [isStatic, setIsStatic] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await budget.categories(true);
    setCategories(r.categories);
  }, []);

  useEffect(() => {
    load().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load.'));
  }, [load]);

  useLiveReload(() => {
    void load().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load.'));
  });

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  async function add(e: React.FormEvent) {
    e.preventDefault();
    let cents: number | undefined;
    if (limit.trim() !== '') {
      const parsed = parseAmountToCents(limit);
      if (parsed === null || parsed < 0) {
        setError('Enter a monthly amount like 400');
        return;
      }
      cents = parsed;
    }
    if (isStatic && (cents === undefined || cents <= 0)) {
      setError('A fixed bill needs a monthly amount.');
      return;
    }
    await run(async () => {
      await budget.addCategory(name.trim(), cents, isStatic);
      setName('');
      setLimit('');
      setIsStatic(false);
    });
  }

  const live = categories.filter((c) => !c.archived);
  const archived = categories.filter((c) => c.archived);

  return (
    <main className="page">
      <h1>Categories</h1>
      {error && <p className="error">{error}</p>}

      <form className="panel" onSubmit={add}>
        <h2>Add a category</h2>
        <label>
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            placeholder="Groceries"
            required
          />
        </label>
        <label>
          {isStatic ? 'Monthly amount' : 'Monthly limit (optional)'}
          <input
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            inputMode="decimal"
            placeholder="400"
          />
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={isStatic}
            onChange={(e) => setIsStatic(e.target.checked)}
          />
          Fixed bill — always counts as spent
        </label>
        <p className="hint">
          Use this for rent, insurance, or anything paid automatically at a known amount.
        </p>
        <button className="primary" disabled={busy || !name.trim()}>
          Add category
        </button>
      </form>

      <section className="panel">
        <h2>Active</h2>
        {live.length === 0 ? (
          <p className="hint">None yet.</p>
        ) : (
          <ul className="list">
            {live.map((c, i) => (
              <li key={c.id}>
                <strong>
                  {c.name}
                  {c.static && <span className="badge">bill</span>}
                </strong>
                <div className="actions">
                  <button
                    className="secondary"
                    disabled={busy || i === 0}
                    aria-label={`Move ${c.name} up`}
                    onClick={() =>
                      void run(async () => {
                        const above = live[i - 1];
                        if (!above) return;
                        // Swap the two orders so the pair trades places.
                        await budget.updateCategory(c.id, { sortOrder: above.sortOrder });
                        await budget.updateCategory(above.id, { sortOrder: c.sortOrder });
                      })
                    }
                  >
                    ↑
                  </button>
                  <button
                    className="secondary"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        const next = window.prompt('Rename category', c.name);
                        if (next && next.trim() && next.trim() !== c.name) {
                          await budget.updateCategory(c.id, { name: next.trim() });
                        }
                      })
                    }
                  >
                    Rename
                  </button>
                  <button
                    className="secondary"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        await budget.updateCategory(c.id, { static: !c.static });
                      })
                    }
                  >
                    {c.static ? 'Not a bill' : 'Make bill'}
                  </button>
                  <button
                    className="danger"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        await budget.updateCategory(c.id, { archived: true });
                      })
                    }
                  >
                    Archive
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {archived.length > 0 && (
        <section className="panel">
          <h2>Archived</h2>
          <p className="hint">
            Hidden from new expenses, but their past spending still shows in the months it happened.
          </p>
          <ul className="list">
            {archived.map((c) => (
              <li key={c.id}>
                <strong className="muted-text">
                  {c.name}
                  {c.static && <span className="badge muted">bill</span>}
                </strong>
                <div className="actions">
                  <button
                    className="secondary"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        await budget.updateCategory(c.id, { archived: false });
                      })
                    }
                  >
                    Restore
                  </button>
                  <button
                    className="danger"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        if (!window.confirm(`Permanently delete ${c.name}?`)) return;
                        await budget.deleteCategory(c.id);
                      })
                    }
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
